import { createSlice, createSelector, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from '../store';

// A single preview entry in the multi-preview map
export interface PreviewEntry {
    content: any;           // Plate editor content (if set via setPreviewContent)
    markdown: string | null; // Markdown string (if set via setPreviewMarkdown)
    isStreaming: boolean;    // Whether this preview is still streaming
}

// Build a composite key for the previews map
export function makePreviewKey(contextKey: string, sectionId: string): string {
    return `${contextKey}::${sectionId}`;
}

// Define the state interface
interface ActiveEditorState {
    activeTabIndex: number;
    activeTabName: string;
    activeTabSectionId: string | null;
    activeContextKey: string | null;
    activeEditorContent: any;
    isDirty: boolean;
    // Multi-preview map: keyed by "contextKey::sectionId"
    previews: Record<string, PreviewEntry>;
    // Which preview key is currently being streamed (only one at a time)
    activeStreamingKey: string | null;
    editorSurface: "ide" | "rich-text" | null;
}

// Define initial state
const initialState: ActiveEditorState = {
    activeTabIndex: 0,
    activeTabName: "",
    activeTabSectionId: null,
    activeContextKey: null,
    activeEditorContent: null,
    isDirty: false,
    previews: {},
    activeStreamingKey: null,
    editorSurface: null
};

// Helper to resolve the preview key from action payload or current state
function resolveKey(
    state: ActiveEditorState,
    payload?: { contextKey?: string; sectionId?: string }
): string {
    const ck = payload?.contextKey || state.activeContextKey || "";
    const sid = payload?.sectionId || state.activeTabSectionId || "";
    return makePreviewKey(ck, sid);
}

// Create the slice
const activeEditorSlice = createSlice({
    name: "activeEditor",
    initialState,
    reducers: {
        setActiveTab: (state, action: PayloadAction<{ index: number; name: string; sectionId?: string; contextKey?: string }>) => {
            state.activeTabIndex = action.payload.index;
            state.activeTabName = action.payload.name;
            state.activeTabSectionId = action.payload.sectionId || null;
            if (action.payload.contextKey) {
                state.activeContextKey = action.payload.contextKey;
            }
            state.isDirty = false;
        },
        setActiveTabByName: (state, action: PayloadAction<{ name: string; sectionId?: string }>) => {
            state.activeTabName = action.payload.name;
            state.activeTabSectionId = action.payload.sectionId || null;
            state.isDirty = false;
        },
        setActiveEditorContent: (state, action: PayloadAction<any>) => {
            state.activeEditorContent = action.payload;
            state.isDirty = true;
        },
        clearDirtyFlag: (state) => {
            state.isDirty = false;
        },
        resetActiveEditor: (state) => {
            state.activeTabIndex = 0;
            state.activeTabName = "";
            state.activeTabSectionId = null;
            state.activeContextKey = null;
            state.activeEditorContent = null;
            state.isDirty = false;
            state.previews = {};
            state.activeStreamingKey = null;
            state.editorSurface = null;
        },
        setEditorSurface: (state, action: PayloadAction<"ide" | "rich-text">) => {
            state.editorSurface = action.payload;
        },
        clearEditorSurface: (state) => {
            state.editorSurface = null;
        },

        // Preview actions — upsert into the previews map

        setPreviewContent: (state, action: PayloadAction<{ content: any; sectionId?: string; contextKey?: string }>) => {
            const key = resolveKey(state, action.payload);
            if (!state.previews[key]) {
                state.previews[key] = { content: null, markdown: null, isStreaming: true };
            }
            state.previews[key].content = action.payload.content;
            state.previews[key].isStreaming = true;
        },

        setPreviewMarkdown: (state, action: PayloadAction<{ markdown: string; sectionId?: string; contextKey?: string }>) => {
            const key = resolveKey(state, action.payload);
            if (!state.previews[key]) {
                state.previews[key] = { content: null, markdown: null, isStreaming: true };
            }
            state.previews[key].markdown = action.payload.markdown;
            state.previews[key].isStreaming = true;
        },

        acceptPreview: (state, action: PayloadAction<{ contextKey?: string; sectionId?: string } | undefined>) => {
            const key = resolveKey(state, action.payload ?? undefined);
            const preview = state.previews[key];
            if (preview) {
                if (preview.content) {
                    state.activeEditorContent = preview.content;
                }
                state.isDirty = true;
                delete state.previews[key];
                if (state.activeStreamingKey === key) {
                    state.activeStreamingKey = null;
                }
            }
        },

        cancelPreview: (state, action: PayloadAction<{ contextKey?: string; sectionId?: string } | undefined>) => {
            const key = resolveKey(state, action.payload ?? undefined);
            delete state.previews[key];
            if (state.activeStreamingKey === key) {
                state.activeStreamingKey = null;
            }
        },

        startStreamingUpdate: (state, action: PayloadAction<{ contextKey?: string; sectionId?: string } | undefined>) => {
            const key = resolveKey(state, action.payload ?? undefined);
            state.activeStreamingKey = key;
            if (!state.previews[key]) {
                state.previews[key] = { content: null, markdown: null, isStreaming: true };
            } else {
                state.previews[key].isStreaming = true;
            }
        },

        endStreamingUpdate: (state, action: PayloadAction<{ contextKey?: string; sectionId?: string } | undefined>) => {
            const key = resolveKey(state, action.payload ?? undefined);
            if (state.previews[key]) {
                state.previews[key].isStreaming = false;
            }
            if (state.activeStreamingKey === key) {
                state.activeStreamingKey = null;
            }
        },

        clearAllPreviews: (state) => {
            state.previews = {};
            state.activeStreamingKey = null;
        }
    }
});

// Export actions and reducer
export const {
    setActiveTab,
    setActiveTabByName,
    setActiveEditorContent,
    clearDirtyFlag,
    resetActiveEditor,
    setPreviewContent,
    setPreviewMarkdown,
    acceptPreview,
    cancelPreview,
    startStreamingUpdate,
    endStreamingUpdate,
    clearAllPreviews
} = activeEditorSlice.actions;
export const setEditorSurface = activeEditorSlice.actions.setEditorSurface;
export const clearEditorSurface = activeEditorSlice.actions.clearEditorSurface;
export default activeEditorSlice.reducer;

// ── Selectors ──────────────────────────────────────────────────────────

export const selectActiveEditor = (state: RootState) => state.activeEditor;
export const selectActiveTabIndex = (state: RootState) => state.activeEditor.activeTabIndex;
export const selectActiveTabName = (state: RootState) => state.activeEditor.activeTabName;
export const selectActiveTabSectionId = (state: RootState) => state.activeEditor.activeTabSectionId;
export const selectActiveContextKey = (state: RootState) => state.activeEditor.activeContextKey;
export const selectActiveEditorContent = (state: RootState) => state.activeEditor.activeEditorContent;
export const selectIsEditorDirty = (state: RootState) => state.activeEditor.isDirty;
export const selectEditorSurface = (state: RootState) => state.activeEditor.editorSurface;

// Base selectors for the previews map
const selectPreviews = (state: RootState) => state.activeEditor.previews;

// Factory: creates a memoized selector for a specific (contextKey, sectionId) pair.
// Use with useMemo in components to avoid re-creating on every render.
export const makeSelectPreviewForSection = (contextKey: string, sectionId: string) => {
    const key = makePreviewKey(contextKey, sectionId);
    return createSelector(
        [selectPreviews],
        (previews): PreviewEntry | undefined => previews[key]
    );
};

// Selector for the preview of the currently active section (backward-compatible)
const selectPreviewForCurrentSection = createSelector(
    [selectPreviews, selectActiveContextKey, selectActiveTabSectionId],
    (previews, contextKey, sectionId): PreviewEntry | undefined => {
        if (!contextKey || !sectionId) return undefined;
        return previews[makePreviewKey(contextKey, sectionId)];
    }
);

// Backward-compatible selectors (derive from active section's preview)
export const selectPreviewContent = createSelector(
    [selectPreviewForCurrentSection],
    (preview) => preview?.content ?? null
);
export const selectPreviewMarkdown = createSelector(
    [selectPreviewForCurrentSection],
    (preview) => preview?.markdown ?? null
);
export const selectShowPreview = createSelector(
    [selectPreviewForCurrentSection],
    (preview) => preview != null
);
export const selectIsStreamingUpdate = createSelector(
    [selectPreviewForCurrentSection],
    (preview) => preview?.isStreaming ?? false
);

// Legacy selectors kept for backward compatibility
export const selectPreviewForSectionId = (state: RootState) => state.activeEditor.activeTabSectionId;
export const selectPreviewForContextKey = (state: RootState) => state.activeEditor.activeContextKey;
